import Button from '@ui/buttons/Button'
import { ModalBottom } from '@ui/components/Modal'
import DetailRow from '@ui/elements/DetailRow'
import { getVariableIcon } from '@ui/elements/Icon/maps'
import PriceInfo from '@/price/PriceInfo'
import { StepOption } from '@order/hooks/useOrderStep'
import { Discounts } from '@order/utils/discounts/Discounts'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import { getFirstExistingKey, joinPaths } from 'utils/i18nUtils'

export interface OptionUpgradeModalProps {
  open: boolean
  option: StepOption | null
  source: StepOption | null
  onUpgrade: () => void
  onClose: () => void
  onSkip: () => void
}

const keys = ['title', 'desc'] as const

type UpgradeTranslations = Record<(typeof keys)[number], string>

export default function OptionUpgradeModal({
  open,
  option,
  source,
  onUpgrade,
  onClose,
  onSkip,
}: OptionUpgradeModalProps) {
  const { t, i18n } = useTranslation(['ui', 'upgrades'])
  const customPathPrefix = option?.active.translationPath
    ? `upgrades:${option.variable}.${option.active.translationPath}`
    : undefined

  const valuePrefix = option?.active.value
    ? `upgrades:${option.variable}.${option.active.value}`
    : undefined
  const basePrefix = `upgrades:default`

  const translations = useMemo(() => {
    const entries = keys.map((key) => {
      const paths = [
        joinPaths([customPathPrefix, key]),
        joinPaths([valuePrefix, key]),
        joinPaths([basePrefix, key]),
      ]

      const tl = getFirstExistingKey(i18n, paths)

      return [key, tl ? t(tl) : undefined]
    })
    // Can we skip type cast somehow?
    return Object.fromEntries(entries) as UpgradeTranslations
  }, [customPathPrefix, i18n, valuePrefix, basePrefix, t])

  if (!option || !source) {
    return null
  }
  const newPrice = Discounts.unwrap(option.active.price)
  const basePrice = Discounts.unwrap(source?.active?.price)
  const priceDiff = Math.max(newPrice - basePrice, 0)

  return (
    <ModalBottom
      open={open}
      close={onClose}
      title={translations.title}
      desc={translations.desc}
      body={
        <View className="flex  flex-col px-16">
          <View className="overflow-hidden rounded-2xl border border-black/10">
            <DetailRow
              icon={getVariableIcon(option.variable)}
              className="border-none"
              desc={option.active.title}
            >
              <PriceInfo value={priceDiff} size="default" />
            </DetailRow>
          </View>
        </View>
      }
      buttons={
        <View className="flex flex-row items-start gap-8">
          <Button
            variant="filled"
            onPress={onUpgrade}
            label={t('ui:upgradeNow')}
          />
          <Button variant="outlined" onPress={onSkip} label={t('ui:skip')} />
        </View>
      }
    />
  )
}
